import { useGlobalEvent, useGlobalStore } from '@highlight/ipc/react'
import { GlobalStore } from '@highlight/ipc'
import { useGoogleCalendarConnection } from '@renderer/hooks/useGoogleCalendarConnection'
import { useCalendars } from '@renderer/hooks/use-calendars'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { Toast } from '../components/toast'
import { useToast } from '../use-toast'
import { Logo } from '@highlight/ui'

type ToastApi = ReturnType<typeof useToast>

export function useCalendarToasts(toast: ToastApi) {
  const [calendarEventEnabled] = useGlobalStore('settings', 'notifications.calendarEvent', true)
  const { onConnect: connectCalendar } = useGoogleCalendarConnection({
    eventAction: 'connect_from_meeting_ended_toast',
  })
  const {
    googleAccounts,
    outlookAccounts,
    pendingAccounts,
    expiredAccounts,
    isSuccess: isCalendarsLoaded,
  } = useCalendars()
  // A mid-sync or expired account is still an account: prompting "Connect"
  // would start a second OAuth grant for one the user already has.
  const hasAnyCalendarAccount =
    googleAccounts.length + outlookAccounts.length + pendingAccounts.length + expiredAccounts.length > 0

  const showConnectCalendarToast = () => {
    // Both the calendars and connections queries must have succeeded before
    // an empty list means "no calendars". On an error or a cold cache,
    // `isCalendarsLoaded` is false and we do nothing this cycle rather than
    // fabricate a disconnect prompt.
    if (!isCalendarsLoaded || hasAnyCalendarAccount || !calendarEventEnabled) return

    const lastPrompted = GlobalStore.conversations().get<number>('calendar.lastPrompted', 0)
    const threeDaysMs = 3 * 24 * 60 * 60 * 1000
    const now = Date.now()

    if (now - lastPrompted > threeDaysMs) {
      GlobalStore.conversations().set('calendar.lastPrompted', now)

      toast.add({
        analytics: { type: 'connect calendar' },
        icon: <Logo name="google-calendar" size={16} />,
        title: 'Connect Your Calendar',
        duration: 15000,
        autoExpandDelayMs: 0,
        autoCollapseDelayMs: 5000,
        body: (toastId) => (
          <Toast toastId={toastId}>
            <Toast.Content>
              <Toast.Description>Highlight can remind you of upcoming meetings.</Toast.Description>
              <Toast.Actions
                actions={[
                  {
                    onClick: () => {
                      toast.dismiss(toastId)
                      connectCalendar()
                      AmplitudeRenderer.trackEvent('Connect Calendar Toast', { action: 'Connect' })
                    },
                    children: 'Connect Calendar',
                    variant: 'primary',
                  },
                ]}
              />
            </Toast.Content>
          </Toast>
        ),
      })

      AmplitudeRenderer.trackEvent('Connect Calendar Toast', { action: 'Show' })
    }
  }

  useGlobalEvent('meeting:ended', showConnectCalendarToast)
  useGlobalEvent('calendar:browsing', showConnectCalendarToast)
}
